/**
 * #380 stage 4.1 (#1900) — client wrappers for the impersonation session's
 * own callables (`functions/permissions/session.js`).
 * Design: .specify/specs/380-admin-permissions-billing/impersonation-design.md §2
 *
 * `actionId` is required by every one of these callables (it doubles as the
 * server's idempotency key and, for `startImpersonation`, becomes the
 * permanent `sessionId`) — generated here, once per call, never reused
 * across a retry the USER initiates (a genuine new click is a new action;
 * only the SDK's own transport-level retry of the same request reuses one).
 */
import { httpsCallable } from '../firebase/app'

/**
 * Same fallback-safe id generator as `leaveRequests.js`/`stations.js`/
 * `variableComponents.js` — each file keeps its own copy rather than a
 * shared util (repo convention).
 */
export function newActionId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/** §2.1/§2.2/§2.3 — starts a new session, always in VIEW. */
export async function callStartImpersonation({ employerId, uid = null, actionId = newActionId() }) {
  const startImpersonation = httpsCallable('startImpersonation')
  const result = await startImpersonation({ employerId, uid, actionId })
  return result.data
}

/** §2.3 — explicit VIEW→EDIT; requires a non-empty reason and confirm. */
export async function callElevateImpersonation({ reason, confirm, actionId = newActionId() }) {
  const elevateImpersonation = httpsCallable('elevateImpersonation')
  const result = await elevateImpersonation({ reason, confirm, actionId })
  return result.data
}

/** The explicit manual EDIT→VIEW transition. */
export async function callLowerToView({ actionId = newActionId() } = {}) {
  const lowerToView = httpsCallable('lowerToView')
  const result = await lowerToView({ actionId })
  return result.data
}

/**
 * Ends the whole session. `targetActorUid` defaults server-side to the
 * caller's own — only set it explicitly for the §6 "any admin may end
 * another admin's stuck session" recovery path.
 */
export async function callEndImpersonation({ targetActorUid, actionId = newActionId() } = {}) {
  const endImpersonation = httpsCallable('endImpersonation')
  const result = await endImpersonation({ targetActorUid, actionId })
  return result.data
}
